import ApiProtectedRequest from "utils/apiProtectedClient";
import { buildUrl } from "utils/buildUrl";
import { Goal } from "./types/aba";

const EP_BASE = "/api";

export type TargetDisposition =
  | { target_id: number; action: "archive" | "delete" }
  | { target_id: number; action: "move"; destination_goal_id: number };

export async function getListOfGoals(clientId: string, params: { includeArchived?: boolean }) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  const url = buildUrl(`${EP_BASE}/clients/${clientId}/goals`, params);
  return await Request.get<Goal[]>(url);
}

export async function getGoalDetails(
  goalId: number,
  params: { includeSessions?: boolean } = { includeSessions: true }
) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  const url = buildUrl(`${EP_BASE}/goals/${goalId}`, params);
  return await Request.get<Goal>(url);
}

export async function createGoal(clientId: string, goalData: Partial<Goal>) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.post<Goal>(`${EP_BASE}/clients/${clientId}/goals`, goalData);
}

export async function updateGoal(goalId: number, goalData: Partial<Goal>) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.put<Goal>(`${EP_BASE}/goals/${goalId}`, goalData);
}

export async function deleteGoal(
  goalId: number,
  rationale: string,
  targetDispositions?: TargetDisposition[]
) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  if (targetDispositions !== undefined) {
    return await Request.post(`${EP_BASE}/goals/${goalId}/delete`, {
      targetDispositions,
      rationale,
    });
  }
  return await Request.delete(`${EP_BASE}/goals/${goalId}`);
}

export async function archiveGoal(
  goalId: number,
  rationale?: string,
  targetDispositions?: TargetDisposition[]
) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  if (rationale !== undefined && !rationale.trim()) {
    throw new Error("archiveGoal requires a non-empty rationale");
  }

  if (targetDispositions !== undefined) {
    if (rationale === undefined) {
      throw new Error("archiveGoal requires a non-empty rationale");
    }

    return await Request.post(`${EP_BASE}/goals/${goalId}/archive`, {
      targetDispositions,
      rationale,
    });
  }

  if (rationale === undefined) {
    return await Request.post(`${EP_BASE}/goals/${goalId}/archive`);
  }

  return await Request.post(`${EP_BASE}/goals/${goalId}/archive`, { rationale });
}

export async function unarchiveGoal(goalId: number) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.post(`${EP_BASE}/goals/${goalId}/unarchive`);
}

export async function updateGoalStatus(
  goalId: number,
  payload: { new_status: string; rationale: string; changed_by?: string | null }
) {
  const apr = new ApiProtectedRequest();
  const Request = apr.getRequest();

  return await Request.post(`${EP_BASE}/goals/${goalId}/status`, { ...payload });
}
